import React, { useState, useEffect, Suspense } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import './App.css';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { ThemeProvider as CustomThemeProvider, useAppTheme } from './ThemeContext';
import { createAppTheme } from './theme/theme';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import userContext from './userContext';
import PrivateRoute from './pages/PrivateRoute';
import Layout from "./pages/Layout";
import { FeatherClient } from './Services/FeatherClient';
import { clientname_for_api } from './components/common';

let appVersion = localStorage.getItem('app_version') || '';
let hasFetchedVersion = false;

export const fetchAppVersion = async (clientname_for_api) => {
  // If appVersion is already loaded in this session, skip fetching to prevent redundant calls/rate limits
  if (hasFetchedVersion && appVersion) {
    if (!localStorage.getItem('app_version')) {
      localStorage.setItem('app_version', appVersion);
    }
    return appVersion;
  }

  try {
    const feathersClient = await FeatherClient();
    const appVersionService = feathersClient.service('clients');
    const response = await appVersionService.find({
      headers: {
        'x-tenant-id': 'multitenant_maindb',
      },
    });
    console.log('appVersion response', response);

    // Feathers service.find may return an array directly or an object with a data array
    const clientsList = Array.isArray(response)
      ? response
      : (response && Array.isArray(response.data) ? response.data : []);

    const respo = clientsList.find((item) => item.client_code === clientname_for_api);
    console.log('respo', respo?.application_version);
    
    // Dynamic fallback version
    const finalVersion = respo?.application_version;
    
    if (finalVersion) {
      appVersion = finalVersion;
      localStorage.setItem('app_version', finalVersion);
    } else {
      appVersion = '';
      localStorage.removeItem('app_version');
    }
    hasFetchedVersion = true;

    if (!respo || !respo.application_version) {
      console.warn('⚠️ clientname_for_api or application_version not found in appVersion response. Falling back to dynamic version:', finalVersion);
    }
  } catch (error) {
    console.error('❌ Error fetching appVersion:', error);
  }
};

const Login = React.lazy(() => import("./pages/auth/Login"));
const ProfilePage = React.lazy(() => import("./pages/appointment/ProfilePage"));
const Appointment = React.lazy(() => import("./pages/appointment/Appointment"));
const UpcomingandPastAppointment = React.lazy(() => import('./pages/appointment/UpcomingandPastAppointment'));
const Laboratory = React.lazy(() => import('./pages/lab/Laboratory'));
const Radiology = React.lazy(() => import('./pages/Radiology'));
const Medication = React.lazy(() => import('./pages/Medication'));
const ProfileAuth = React.lazy(() => import('./pages/appointment/ProfileAuth'));
const CreateProfile = React.lazy(() => import('./pages/appointment/CreateProfile'));
const GetProfile = React.lazy(() => import('./pages/appointment/GetProfile'));
const Treatment = React.lazy(() => import('./pages/Treatment'));
const PatientFolder = React.lazy(() => import('./pages/PatientFolder'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const SubmitRequest = React.lazy(() => import('./pages/SubmitRequest'));
const DoctorDetail = React.lazy(() => import('./pages/DoctorDetail'));
const FamilyMember = React.lazy(() => import('./pages/FamilyMember'));
const EditFamilyMember = React.lazy(() => import('./pages/EditFamilyMember'));
const AddFamilyMember = React.lazy(() => import('./pages/AddFamilyMember'));
const Review = React.lazy(() => import('./pages/Review'));
const FamilyPatientAppointment = React.lazy(() => import('./pages/FamilyPatientAppointment'));
const IntakeForm = React.lazy(() => import('./pages/IntakeForm'));
const AIHealthAssistant = React.lazy(() => import('./pages/AIHealthAssistant'));
const LabTrendMockup = React.lazy(() => import('./pages/lab/LabTrendMockup'));

const protectedRoutes = [
  { path: 'profile-auth', element: <ProfileAuth /> },
  { path: 'appointment', element: <Appointment /> },
  { path: 'doctor-detail', element: <DoctorDetail /> },
  { path: 'create-profile', element: <CreateProfile /> },
  { path: 'get-profile', element: <GetProfile /> },
  { path: 'complete-profile', element: <ProfilePage /> },
  { path: 'app-details', element: <UpcomingandPastAppointment /> },
  { path: 'laboratory', element: <Laboratory /> },
  { path: 'radiology', element: <Radiology /> },
  { path: 'patient-folder', element: <PatientFolder /> },
  { path: 'contact-us', element: <ContactUs /> },
  { path: 'submit-request', element: <SubmitRequest /> },
  { path: 'family-member', element: <FamilyMember /> },
  { path: 'edit-family-member', element: <EditFamilyMember /> },
  { path: 'add-family-member', element: <AddFamilyMember /> },
  { path: 'review', element: <Review /> },
  { path: 'family-patient-appointment', element: <FamilyPatientAppointment /> },
  { path: 'my-medication', element: <Medication /> },
  { path: 'treatment', element: <Treatment /> },
  { path: 'intake-form', element: <IntakeForm /> },
  { path: 'ai-assistant', element: <AIHealthAssistant /> },
  { path: 'lab-trend', element: <LabTrendMockup /> },
].filter(Boolean);

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/login" replace /> },
      ...protectedRoutes.map(route => ({
        path: route.path,
        element: <PrivateRoute element={route.element} />
      }))
    ]
  },
  { path: '/login', element: <Login /> },
  { path: '*', element: <Navigate to="/login" replace /> },
]);

function App() {
  const [user, setUser] = useState('');
  const [isVersionLoaded, setIsVersionLoaded] = useState(false);

  useEffect(() => {
    const initAppVersion = async () => {
      await fetchAppVersion(clientname_for_api);
      setIsVersionLoaded(true);
    };
    initAppVersion();
  }, []);

  const { primaryColor } = useAppTheme();
  const dynamicTheme = createAppTheme(primaryColor);

  return (
    <MuiThemeProvider theme={dynamicTheme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <userContext.Provider value={[user, setUser]}>
          <CssBaseline />
          {!isVersionLoaded ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
              <CircularProgress color="primary" />
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
          ) : (
            <Suspense fallback={
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
                <CircularProgress color="primary" />
                <Typography variant="body2" color="text.secondary">Loading...</Typography>
              </Box>
            }>
              <RouterProvider router={router} /> {/* 🔹 CHANGED */}
            </Suspense>
          )}
        </userContext.Provider>
      </LocalizationProvider>
    </MuiThemeProvider>
  );
}

const RootApp = () => (
  <CustomThemeProvider>
    <App />
  </CustomThemeProvider>
);

export default RootApp;

